import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
    TextField,
    Tooltip,
} from '@mui/material';
import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
    useMemo,
} from 'react';
import QuestionNewEditForm from './components/question-new-edit-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { common } from '@mui/material/colors';
import { colors } from 'common/constants';
import { createStyleSheet, handleError } from 'utils/helpers';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
// import { ReactComponent as Document } from 'assets/images/icons/upload-file/document-valid.svg';
import { ReactComponent as AddQuestionIcon } from 'assets/images/icons/question-design/plus-question.svg';
import { IQuestionNewEditForm } from 'types/campaign-design';
import { ReactComponent as EditCampaign } from 'assets/images/icons/question-design/pencil-question.svg';
import { ReactComponent as SaveQuestionIcon } from 'assets/images/icons/question-design/save-question.svg';
import ModalUplooadFileQuestion from './components/upload-file-question/modal-upload-file-question';
import {
    createCampaignQuestion,
    updateCampaignQuestion,
    swapCampaignQuestion,
    deleteCampaignQuestion,
    markCampaignQuestionAsRequired,
    duplicateCampaignQuestion,
} from 'services/campaign.service';
import { useNavigate, useParams } from 'react-router-dom';
import { listCampaignQuestionsFormatter } from 'utils/campaign_helpers';
import ModalConfirmSwapQuestion from './components/swap-position/modal-confirm-swap-question';
import { useAppDispatch, useAppSelector } from 'common/hook';
import { RootState } from 'plugins/store';
import { setSelectedQuestionIndex } from 'slices/campaignsSlice';
import { toast } from 'react-toastify';
import { CampaignContainer } from '../campaign-overview/campaign-container';
import CampaignForm from '../campaign/components/campaign-form';
import CampaignStatus from '../campaign/components/campaign-status';
import { setIsLoading } from 'slices/loadingSlice';
import {
    IQuestionPage,
    listQuestionsPageLogic,
    createPageLogic,
    deletePageLogic,
    updatePageLogic,
} from 'services/campaignPageLogic.service';
import ModalConfirm from 'common/components/modal-confirm';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import NumbersIcon from '@mui/icons-material/Numbers';
import RewardForm from './components/reward/reward-form';
import QuotaForm from './components/quota/quota-form';

const DEFAULT_FIRST_QUESTION = {
    id: '',
    question: '',
    questionType: 'textbox',
    data: null,
    required: false,
    questionNumber: 1,
};

const DEFAULT_FIRST_QUESTION_PUBLIC_WITH_CODE = {
    id: '',
    question: '',
    questionType: 'single_choice',
    data: {
        answers: [
            {
                answer: 'Tùy chọn 1',
                id: null,
                image: null,
                type: '1',
            },
        ],
        numberOfRows: 1,
    },
    required: false,
    questionNumber: 1,
};

const DEFAULT_QUESTION_CONTENT = 'Nhập nội dung câu hỏi?';

const CampaignDesignPageLogic: React.FC = () => {
    const currentCampaignId = Number(useParams().id);
    const [pageToDelete, setPageToDelete] = useState<IQuestionPage>(
        {} as IQuestionPage
    );
    const [selectedPageId, setSelectedPageId] = useState<string>('');
    const [editingPageTitle, setEditingPageTitle] = useState<string>('');
    const [openModalConfirmDeletePage, setOpenModalConfirmDeletePage] =
        useState(false);
    const [questionPages, setQuestionPages] = useState<IQuestionPage[]>([]);
    const [editingPageId, setEditingPageId] = useState<string>('');

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedIndex = useAppSelector(
        (state: RootState) => state.campaignState.selectedQuestionIndex
    );
    const [openUploadFileExcel, setOpenUploadFileExcel] = React.useState(false);

    const [isModalOpenDrag, setIsModalOpenDrag] = useState(false);
    const [dragSource, setDragSource] = useState<IQuestionNewEditForm | null>(
        null
    );
    const currentCampaign = useAppSelector(
        (state) => state.campaignState.campaign
    );
    const [openModalCampaign, setOpenModalCampaign] =
        React.useState<boolean>(false);
    const [dragTarget, setDragTarget] = useState<IQuestionNewEditForm | null>(
        null
    );

    const isCampaignPublicWithCode = useMemo(() => {
        return currentCampaign && currentCampaign?.kind === 'public_with_code';
    }, [currentCampaign?.kind]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const methodsRef = useRef<any[]>([]);

    // Lấy danh sách câu hỏi của page hiện tại (đã format)
    const currentPageQuestions = useMemo(() => {
        const page = questionPages.find((p) => p.id === selectedPageId);
        if (!page) return [];

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return page.attributes.questions.map((question: any) => {
            // Nếu question đã được format (không có attributes), trả về trực tiếp
            if (!question.attributes && question.questionType) {
                return question;
            }
            // Nếu question từ API (có attributes), format nó
            return listCampaignQuestionsFormatter(question);
        });
    }, [questionPages, selectedPageId]);

    // Fetch question pages
    const fetchQuestionPages = useCallback(
        async (keepCurrentPage = false) => {
            try {
                dispatch(setIsLoading(true));
                const response =
                    await listQuestionsPageLogic(currentCampaignId);
                if (response.success) {
                    const pages = response.data.question_pages;
                    setQuestionPages(pages);
                    // Tự động chọn page đầu tiên nếu có và chưa chọn
                    if (!keepCurrentPage) {
                        if (pages.length > 0 && !selectedPageId) {
                            setSelectedPageId(pages[0].id);
                        }
                    } else {
                        // Kiểm tra xem page hiện tại có còn tồn tại không
                        const currentPageExists = pages.some(
                            (p: IQuestionPage) => p.id === selectedPageId
                        );
                        if (!currentPageExists && pages.length > 0) {
                            setSelectedPageId(pages[0].id);
                        }
                    }
                }
                dispatch(setIsLoading(false));
            } catch (error) {
                dispatch(setIsLoading(false));
                handleError(error);
            }
        },
        [currentCampaignId, dispatch, selectedPageId]
    );

    useEffect(() => {
        if (currentCampaign) {
            fetchQuestionPages(false);
        }
    }, [currentCampaign, currentCampaignId]);

    useEffect(() => {
        // Scroll element into view based on selectedIndex
        const element = document.getElementById(`question-${selectedIndex}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [selectedIndex]);

    // ==================== HELPER: Cập nhật questions trong page hiện tại ====================
    const updateCurrentPageQuestions = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (updater: (questions: any[]) => any[]) => {
            setQuestionPages((prevPages) =>
                prevPages.map((page) => {
                    if (page.id === selectedPageId) {
                        return {
                            ...page,
                            attributes: {
                                ...page.attributes,
                                questions: updater(page.attributes.questions),
                            },
                        };
                    }
                    return page;
                })
            );
        },
        [selectedPageId]
    );

    // ==================== QUESTION HANDLERS ====================

    const handleItemSelect = async (index: number) => {
        if (selectedIndex === index) {
            return;
        } else if (selectedIndex === null || selectedIndex === undefined) {
            dispatch(setSelectedQuestionIndex(index));
            return;
        } else {
            dispatch(setIsLoading(true));
            const callback = () => {
                dispatch(setSelectedQuestionIndex(index));
                dispatch(setIsLoading(false));
            };
            checkCurrentSelectQuestionAndSubmit(callback);
        }
    };

    const addQuestionToList = (newQuestion: IQuestionNewEditForm) => {
        if (!selectedPageId) {
            toast.error('Vui lòng chọn một trang trước');
            return false;
        }

        updateCurrentPageQuestions((questions) => {
            let newQuestions;
            if (
                selectedIndex !== null &&
                selectedIndex !== undefined &&
                selectedIndex < questions.length
            ) {
                newQuestions = [
                    ...questions.slice(0, selectedIndex + 1),
                    newQuestion,
                    ...questions.slice(selectedIndex + 1),
                ];
            } else {
                newQuestions = [...questions, newQuestion];
            }
            return newQuestions;
        });

        // Cập nhật selectedIndex
        if (selectedIndex !== null && selectedIndex !== undefined) {
            dispatch(setSelectedQuestionIndex(selectedIndex + 1));
        } else {
            dispatch(setSelectedQuestionIndex(currentPageQuestions.length));
        }

        return true;
    };

    const handleAddQuestion = async () => {
        if (!selectedPageId) {
            toast.error('Vui lòng chọn một trang trước');
            return;
        }

        dispatch(setIsLoading(true));

        const callback = () => {
            const currentPage = questionPages.find(
                (p) => p.id === selectedPageId
            );
            const questions = currentPage?.attributes?.questions || [];

            // Format questions để lấy questionNumber
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const formattedQuestions = questions.map((q: any) => {
                if (!q.attributes && q.questionType) {
                    return q;
                }
                return listCampaignQuestionsFormatter(q);
            });

            let newQuestionNumber: number;

            if (
                selectedIndex !== null &&
                selectedIndex !== undefined &&
                selectedIndex < formattedQuestions.length
            ) {
                // Nếu có câu hỏi đang được chọn, lấy questionNumber của câu đó + 1
                const selectedQuestion = formattedQuestions[selectedIndex];
                newQuestionNumber =
                    (selectedQuestion?.questionNumber || selectedIndex + 1) + 1;
            } else {
                // Nếu không có câu hỏi nào được chọn hoặc mảng rỗng
                if (formattedQuestions.length === 0) {
                    newQuestionNumber = 1;
                } else {
                    // Lấy questionNumber của câu cuối cùng + 1
                    const lastQuestion =
                        formattedQuestions[formattedQuestions.length - 1];
                    newQuestionNumber =
                        (lastQuestion?.questionNumber ||
                            formattedQuestions.length) + 1;
                }
            }

            const baseQuestion = isCampaignPublicWithCode
                ? DEFAULT_FIRST_QUESTION_PUBLIC_WITH_CODE
                : DEFAULT_FIRST_QUESTION;

            const newQuestion = {
                ...baseQuestion,
                id: null,
                questionNumber: newQuestionNumber,
            };

            addQuestionToList(newQuestion);
            dispatch(setIsLoading(false));
        };

        checkCurrentSelectQuestionAndSubmit(callback);
    };

    const handleDeleteQuestion = () => {
        if (selectedIndex === null || selectedIndex === undefined) return;

        const question = currentPageQuestions[selectedIndex];

        const deleteFromState = () => {
            updateCurrentPageQuestions((questions) =>
                questions.filter((_, index) => index !== selectedIndex)
            );
            dispatch(setSelectedQuestionIndex(null));
        };

        const deleteWithAPI = async () => {
            try {
                const deleteResponse = await deleteCampaignQuestion(
                    currentCampaignId,
                    Number(question.id)
                );
                if (deleteResponse.success) {
                    deleteFromState();
                    toast.success(deleteResponse.message);
                } else {
                    toast.error(deleteResponse.message);
                }
            } catch (error) {
                handleError(error);
            }
        };

        if (question.id) {
            deleteWithAPI();
        } else {
            deleteFromState();
        }
    };

    const handleMarkQuestionAsRequired = () => {
        if (selectedIndex === null || selectedIndex === undefined) return;

        const question = currentPageQuestions[selectedIndex];

        const markWithAPI = async () => {
            const markResponse = await markCampaignQuestionAsRequired(
                currentCampaignId,
                Number(question.id)
            );
            if (markResponse.success) {
                toast.success(markResponse.message);
            } else {
                toast.error(markResponse.message);
            }
        };

        if (question.id) {
            markWithAPI();
        }

        // Cập nhật state
        updateCurrentPageQuestions((questions) =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            questions.map((q: any, index: number) => {
                if (index === selectedIndex) {
                    // Nếu question ở dạng raw từ API (có attributes)
                    if (q.attributes) {
                        return {
                            ...q,
                            attributes: {
                                ...q.attributes,
                                required: !q.attributes.required,
                            },
                        };
                    }
                    // Nếu question đã được format
                    return { ...q, required: !q.required };
                }
                return q;
            })
        );
    };

    const replaceQuestionByQuestionId = (
        questionId: number,
        updatedQuestion: IQuestionNewEditForm
    ) => {
        updateCurrentPageQuestions((questions) =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            questions.map((q: any) => {
                const qId = q.id || q.attributes?.id;
                if (Number(qId) === Number(questionId)) {
                    // Trả về question đã được format hoàn toàn (không giữ lại structure cũ)
                    return updatedQuestion;
                }
                return q;
            })
        );
    };

    const onSubmitCurrentQuestion = async (
        data: IQuestionNewEditForm,
        callbackSyncListQuestion?: () => void
    ) => {
        try {
            const submitData = {
                ...data,
                questionPageId: selectedPageId, // Thêm page_id khi submit
            };
            let resSuccess = false;
            let resMessage = '';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let resData: any = null;

            if (
                submitData.questionIdForTitle &&
                submitData.questionIdForTitle !== 0 &&
                submitData.mentionValue
            ) {
                submitData.question = submitData.question.replace(
                    submitData.mentionValue,
                    '$title'
                );
            }

            if (submitData.id) {
                const res = await updateCampaignQuestion(
                    currentCampaignId,
                    submitData
                );
                resSuccess = res.success;
                resMessage = res.message;
                resData = res.data;
            } else {
                const res = await createCampaignQuestion(
                    currentCampaignId,
                    submitData
                );
                resSuccess = res.success;
                resMessage = res.message;
                resData = res.data;
            }

            if (resSuccess) {
                const resQuestion = resData?.question;

                if (resQuestion) {
                    // Cập nhật question trong questionPages
                    updateCurrentPageQuestions((questions) =>
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        questions.map((q: any, index: number) => {
                            if (index === selectedIndex) {
                                return resQuestion;
                            }
                            return q;
                        })
                    );
                }

                if (callbackSyncListQuestion) {
                    callbackSyncListQuestion();
                }
                toast.success(resMessage);
            } else {
                dispatch(setIsLoading(false));
                toast.error(resMessage);
                if (selectedIndex !== null && selectedIndex !== undefined) {
                    methodsRef.current[selectedIndex].setError('isPageConfig', {
                        type: 'server',
                        message: resMessage,
                    });
                    dispatch(setSelectedQuestionIndex(null));

                    // (Tùy chọn) clear method ref để tránh giữ ref cũ
                    if (
                        methodsRef.current &&
                        methodsRef.current[selectedIndex]
                    ) {
                        methodsRef.current[selectedIndex] = null;
                    }
                }
            }
            return resSuccess;
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    };

    const triggerSubmitQuestion = (callbackSyncListQuestion?: () => void) => {
        if (selectedIndex !== null && selectedIndex !== undefined) {
            const selectedMethod = methodsRef.current[selectedIndex];
            if (selectedMethod) {
                selectedMethod.handleSubmit((data: IQuestionNewEditForm) =>
                    onSubmitCurrentQuestion(data, callbackSyncListQuestion)
                )();
            }
        }
    };

    const checkCurrentSelectQuestionAndSubmit = async (
        callbackSyncListQuestion?: () => void
    ) => {
        if (selectedIndex !== null && selectedIndex !== undefined) {
            const selectedMethod = methodsRef.current[selectedIndex];

            if (!selectedMethod) {
                callbackSyncListQuestion && callbackSyncListQuestion();
                return true;
            }

            const { isDirty } = selectedMethod.formState;
            const isValid = await selectedMethod.trigger();

            if (isValid) {
                if (isDirty) {
                    triggerSubmitQuestion(callbackSyncListQuestion);
                } else {
                    callbackSyncListQuestion && callbackSyncListQuestion();
                }
                return true;
            } else {
                const errorElement = document.querySelector(
                    '.Mui-error'
                ) as HTMLElement;
                if (errorElement) {
                    errorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
                dispatch(setIsLoading(false));
                return false;
            }
        } else {
            callbackSyncListQuestion && callbackSyncListQuestion();
            return true;
        }
    };

    const swapQuestion = async (
        from: IQuestionNewEditForm,
        to: IQuestionNewEditForm
    ) => {
        try {
            if (from.id !== to.id) {
                dispatch(setIsLoading(true));
                const swapResponse = await swapCampaignQuestion(
                    currentCampaignId,
                    {
                        from: Number(from.id),
                        to: Number(to.id),
                    }
                );
                if (!swapResponse.success) {
                    toast.error(swapResponse.message);
                } else {
                    toast.success(swapResponse.message);
                    fetchQuestionPages(true); // Reload data
                }
                dispatch(setIsLoading(false));
            }
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    };

    const handleCopyQuestion = async (question: IQuestionNewEditForm) => {
        if (!selectedPageId) return;

        dispatch(setIsLoading(true));

        const callback = async () => {
            try {
                const res = await duplicateCampaignQuestion(
                    currentCampaignId,
                    Number(question.id)
                );
                dispatch(setIsLoading(false));

                if (res.success) {
                    const copiedQuestion = res.data.question;
                    addQuestionToList(
                        listCampaignQuestionsFormatter(copiedQuestion)
                    );
                    toast.success('Sao chép câu hỏi thành công');
                } else {
                    toast.error('Sao chép câu hỏi thất bại');
                }
            } catch (error) {
                dispatch(setIsLoading(false));
                handleError(error);
            }
        };

        checkCurrentSelectQuestionAndSubmit(callback);
    };

    const handleSaveSelectedQuestion = async () => {
        dispatch(setIsLoading(true));

        const callback = () => {
            dispatch(setSelectedQuestionIndex(null));
            dispatch(setIsLoading(false));
        };

        checkCurrentSelectQuestionAndSubmit(callback);
    };

    // ==================== DRAG & DROP ====================

    const handleDragStart =
        (data: IQuestionNewEditForm) => (event: React.DragEvent) => {
            const from = JSON.stringify(data);
            event.dataTransfer.setData('dragContent', from);
        };

    const handleDragOver =
        (data: IQuestionNewEditForm) => (event: React.DragEvent) => {
            if (data) {
                event.preventDefault();
                return false;
            }
            event.preventDefault();
        };

    const handleDrop =
        (data: IQuestionNewEditForm) => (event: React.DragEvent) => {
            event.preventDefault();
            const from = JSON.parse(event.dataTransfer.getData('dragContent'));
            const to = data;

            setDragSource(from);
            setDragTarget(to);
            if (to.setting_logic) {
                return;
            }
            setIsModalOpenDrag(true);
        };

    const confirmSwap = () => {
        if (dragSource && dragTarget) {
            swapQuestion(dragSource, dragTarget);
        }
        closeModal();
    };

    const closeModal = () => {
        setIsModalOpenDrag(false);
        setDragSource(null);
        setDragTarget(null);
    };

    // ==================== PAGE HANDLERS ====================

    const handleAddPage = async () => {
        try {
            const response = await createPageLogic(
                currentCampaignId,
                'Trang mới'
            );
            if (response.success) {
                toast.success(response.message);
                setQuestionPages([
                    ...questionPages,
                    response.data.question_page,
                ]);
                setSelectedPageId(response.data.question_page.id);
                dispatch(setSelectedQuestionIndex(null));
                methodsRef.current = [];
            }
        } catch (error) {
            handleError(error);
        }
    };

    const handleDeletePage = async (id: string) => {
        try {
            const response = await deletePageLogic(id);
            if (response.success) {
                toast.success(response.message);
                const newPages = questionPages.filter((page) => page.id !== id);
                setQuestionPages(newPages);
                setOpenModalConfirmDeletePage(false);

                // Chọn page khác nếu đang xóa page đang chọn
                if (selectedPageId === id && newPages.length > 0) {
                    setSelectedPageId(newPages[0].id);
                }
                dispatch(setSelectedQuestionIndex(null));
                methodsRef.current = [];
            }
        } catch (error) {
            handleError(error);
        }
    };

    const handleSelectPage = (id: string) => {
        if (selectedPageId === id) return;

        const callback = () => {
            setSelectedPageId(id);
            dispatch(setSelectedQuestionIndex(null));
            methodsRef.current = [];
        };

        if (selectedIndex !== null && selectedIndex !== undefined) {
            dispatch(setIsLoading(true));
            checkCurrentSelectQuestionAndSubmit(() => {
                dispatch(setIsLoading(false));
                callback();
            });
        } else {
            callback();
        }
    };

    const handleStartEditPageTitle = (page: IQuestionPage) => {
        setEditingPageId(page.id);
        setEditingPageTitle(page.attributes.title);
    };

    const handleSavePageTitle = async () => {
        if (!editingPageId || !editingPageTitle.trim()) return;

        try {
            const response = await updatePageLogic(
                currentCampaignId,
                editingPageId,
                editingPageTitle.trim()
            );
            if (response.success) {
                toast.success(response.message);
                const updatedPage = response.data.question_page;

                setQuestionPages((prevPages) =>
                    prevPages.map((page) =>
                        page.id === updatedPage.id
                            ? {
                                  ...page,
                                  attributes: {
                                      ...page.attributes,
                                      title: updatedPage.attributes.title,
                                  },
                              }
                            : page
                    )
                );
            }
        } catch (error) {
            handleError(error);
        }

        setEditingPageId('');
        setEditingPageTitle('');
    };

    const handleCancelEditPageTitle = () => {
        setEditingPageId('');
        setEditingPageTitle('');
    };

    // ==================== UTILITIES ====================

    const truncateText = (text: string, maxLength = 20) => {
        if (text.length > maxLength) {
            return text.slice(0, maxLength) + '...';
        }
        return text;
    };

    const [isOpenSetupReward, setIsOpenSetupReward] = useState(false);

    const handleOpenSetupReward = () => {
        setIsOpenSetupReward(true);
    };

    const [isOpenSetupQuota, setIsOpenSetupQuota] = useState(false);
    const handleOpenSetupQuota = () => {
        setIsOpenSetupQuota(true);
    };

    return (
        <CampaignContainer>
            <Grid container spacing={2}>
                <Grid item md={3}>
                    <Box
                        sx={{
                            height: '100%',
                            background: common.white,
                            borderRadius: '15px',
                        }}
                    >
                        <Box
                            sx={{
                                padding: '24px',
                            }}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    bgcolor: 'background.paper',
                                }}
                            >
                                {/* Pages List Header */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '16px',
                                            color: common.black,
                                        }}
                                    >
                                        Danh sách trang ({questionPages.length})
                                    </Typography>
                                    <Tooltip title="Thêm trang mới">
                                        <IconButton
                                            size="small"
                                            onClick={handleAddPage}
                                            sx={{
                                                backgroundColor: colors.main,
                                                color: common.white,
                                                '&:hover': {
                                                    backgroundColor:
                                                        colors.main,
                                                    opacity: 0.9,
                                                },
                                            }}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                {/* Pages with Questions */}
                                <Stack
                                    spacing={1}
                                    sx={{
                                        maxHeight: 'calc(100vh - 250px)',
                                        overflowY: 'auto',
                                    }}
                                >
                                    {questionPages.map((page) => {
                                        // Format questions for this page
                                        const pageQuestions =
                                            page.attributes.questions.map(
                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                (question: any) => {
                                                    if (
                                                        !question.attributes &&
                                                        question.questionType
                                                    ) {
                                                        return question;
                                                    }
                                                    return listCampaignQuestionsFormatter(
                                                        question
                                                    );
                                                }
                                            );

                                        const isSelected =
                                            selectedPageId === page.id;

                                        return (
                                            <Accordion
                                                key={page.id}
                                                expanded={isSelected}
                                                onChange={() =>
                                                    handleSelectPage(page.id)
                                                }
                                                sx={{
                                                    boxShadow: 'none',
                                                    border: isSelected
                                                        ? `2px solid ${colors.main}`
                                                        : '2px solid #E0E0E0',
                                                    borderRadius:
                                                        '10px !important',
                                                    backgroundColor: isSelected
                                                        ? 'var(--color-primary-light-20)'
                                                        : '#F7F7F6',
                                                    '&:before': {
                                                        display: 'none',
                                                    },
                                                    '& .MuiAccordionSummary-root':
                                                        {
                                                            minHeight: '48px',
                                                            '&.Mui-expanded': {
                                                                minHeight:
                                                                    '48px',
                                                            },
                                                        },
                                                    '& .MuiAccordionSummary-content':
                                                        {
                                                            margin: '8px 0',
                                                            '&.Mui-expanded': {
                                                                margin: '8px 0',
                                                            },
                                                        },
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={
                                                        <ExpandMoreIcon
                                                            sx={{
                                                                color: isSelected
                                                                    ? colors.main
                                                                    : '#666',
                                                            }}
                                                        />
                                                    }
                                                    sx={{
                                                        padding: '0 12px',
                                                        borderRadius: '10px',
                                                    }}
                                                >
                                                    {editingPageId ===
                                                    page.id ? (
                                                        <TextField
                                                            size="small"
                                                            value={
                                                                editingPageTitle
                                                            }
                                                            onChange={(e) =>
                                                                setEditingPageTitle(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            onBlur={
                                                                handleSavePageTitle
                                                            }
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key ===
                                                                    'Enter'
                                                                ) {
                                                                    handleSavePageTitle();
                                                                } else if (
                                                                    e.key ===
                                                                    'Escape'
                                                                ) {
                                                                    handleCancelEditPageTitle();
                                                                }
                                                            }}
                                                            autoFocus
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                            sx={{
                                                                flex: 1,
                                                                '& .MuiInputBase-input':
                                                                    {
                                                                        padding:
                                                                            '4px 8px',
                                                                        fontSize:
                                                                            '14px',
                                                                    },
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'space-between',
                                                                width: '100%',
                                                                pr: 1,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight:
                                                                            isSelected
                                                                                ? 600
                                                                                : 400,
                                                                        fontSize:
                                                                            '14px',
                                                                        color: isSelected
                                                                            ? colors.main
                                                                            : common.black,
                                                                    }}
                                                                >
                                                                    {
                                                                        page
                                                                            .attributes
                                                                            .title
                                                                    }
                                                                </Typography>
                                                                <Typography
                                                                    component="span"
                                                                    sx={{
                                                                        fontSize:
                                                                            '11px',
                                                                        backgroundColor:
                                                                            isSelected
                                                                                ? colors.main
                                                                                : '#888',
                                                                        color: common.white,
                                                                        padding:
                                                                            '2px 8px',
                                                                        borderRadius:
                                                                            '10px',
                                                                    }}
                                                                >
                                                                    {
                                                                        pageQuestions.length
                                                                    }
                                                                </Typography>
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    gap: 0.5,
                                                                }}
                                                                onClick={(e) =>
                                                                    e.stopPropagation()
                                                                }
                                                            >
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        handleStartEditPageTitle(
                                                                            page
                                                                        );
                                                                    }}
                                                                    sx={{
                                                                        padding:
                                                                            '4px',
                                                                    }}
                                                                >
                                                                    <EditIcon
                                                                        sx={{
                                                                            fontSize:
                                                                                '16px',
                                                                            color: '#666',
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        setPageToDelete(
                                                                            page
                                                                        );
                                                                        setOpenModalConfirmDeletePage(
                                                                            true
                                                                        );
                                                                    }}
                                                                    sx={{
                                                                        padding:
                                                                            '4px',
                                                                    }}
                                                                    // disabled={
                                                                    //     questionPages.length <=
                                                                    //     1
                                                                    // }
                                                                >
                                                                    <DeleteOutlineIcon
                                                                        sx={{
                                                                            fontSize:
                                                                                '16px',
                                                                            color: '#ff4444',
                                                                            // color:
                                                                            //     questionPages.length <=
                                                                            //     1
                                                                            //         ? '#ccc'
                                                                            //         : '#ff4444',
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </AccordionSummary>
                                                <AccordionDetails
                                                    sx={{
                                                        padding: '8px',
                                                        backgroundColor:
                                                            common.white,
                                                        borderRadius:
                                                            '0 0 8px 8px',
                                                    }}
                                                >
                                                    <List
                                                        component="nav"
                                                        aria-label="list-question"
                                                        sx={{
                                                            maxHeight: '250px',
                                                            overflowY: 'auto',
                                                            padding: 0,
                                                        }}
                                                    >
                                                        {pageQuestions.length ===
                                                        0 ? (
                                                            <Typography
                                                                sx={{
                                                                    fontSize:
                                                                        '13px',
                                                                    color: '#888',
                                                                    textAlign:
                                                                        'center',
                                                                    py: 2,
                                                                }}
                                                            >
                                                                Chưa có câu hỏi
                                                            </Typography>
                                                        ) : (
                                                            pageQuestions.map(
                                                                (
                                                                    question: IQuestionNewEditForm,
                                                                    key: number
                                                                ) => {
                                                                    const isQuestionSelected =
                                                                        selectedIndex ===
                                                                        key;

                                                                    return (
                                                                        <ListItem
                                                                            disablePadding
                                                                            key={
                                                                                key
                                                                            }
                                                                        >
                                                                            <ListItemButton
                                                                                selected={
                                                                                    isQuestionSelected
                                                                                }
                                                                                onClick={() =>
                                                                                    handleItemSelect(
                                                                                        key
                                                                                    )
                                                                                }
                                                                                sx={{
                                                                                    borderRadius:
                                                                                        '25px',
                                                                                    py: 0.5,
                                                                                    px: 1,
                                                                                    background:
                                                                                        isQuestionSelected
                                                                                            ? colors.main +
                                                                                              '20'
                                                                                            : 'transparent',
                                                                                    '&:hover':
                                                                                        {
                                                                                            background:
                                                                                                colors.main +
                                                                                                '10',
                                                                                        },
                                                                                }}
                                                                            >
                                                                                <ListItemIcon
                                                                                    sx={{
                                                                                        minWidth:
                                                                                            '28px',
                                                                                        visibility:
                                                                                            isQuestionSelected
                                                                                                ? 'visible'
                                                                                                : 'hidden',
                                                                                        color: colors.main,
                                                                                    }}
                                                                                >
                                                                                    <CheckIcon
                                                                                        sx={{
                                                                                            fontSize:
                                                                                                '16px',
                                                                                        }}
                                                                                    />
                                                                                </ListItemIcon>
                                                                                <ListItemText
                                                                                    sx={{
                                                                                        '& span':
                                                                                            {
                                                                                                fontSize:
                                                                                                    '13px',
                                                                                                fontWeight:
                                                                                                    isQuestionSelected
                                                                                                        ? 600
                                                                                                        : 400,
                                                                                                color: isQuestionSelected
                                                                                                    ? colors.main
                                                                                                    : common.black,
                                                                                            },
                                                                                    }}
                                                                                >
                                                                                    <Box
                                                                                        sx={{
                                                                                            display:
                                                                                                'flex',
                                                                                            alignItems:
                                                                                                'center',
                                                                                            gap: 0.5,
                                                                                        }}
                                                                                    >
                                                                                        <Tooltip
                                                                                            title={`Q${key + 1}. ${
                                                                                                question.question
                                                                                            }`}
                                                                                            arrow
                                                                                        >
                                                                                            <Typography
                                                                                                sx={{
                                                                                                    fontSize:
                                                                                                        '13px',
                                                                                                }}
                                                                                            >
                                                                                                {`Q${key + 1}. ${
                                                                                                    truncateText(
                                                                                                        question.question
                                                                                                    ) ||
                                                                                                    DEFAULT_QUESTION_CONTENT
                                                                                                }`}
                                                                                            </Typography>
                                                                                        </Tooltip>
                                                                                        {/* {question.required && (
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        color: 'red',
                                                                                                        fontSize:
                                                                                                            '16px',
                                                                                                        fontWeight: 600,
                                                                                                        ml: '4px',
                                                                                                    }}
                                                                                                >
                                                                                                    *
                                                                                                </Box>
                                                                                            )} */}
                                                                                        {question.isPageConfig && (
                                                                                            <InsertLinkIcon
                                                                                                sx={{
                                                                                                    color: colors.main,
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                    </Box>
                                                                                </ListItemText>
                                                                            </ListItemButton>
                                                                        </ListItem>
                                                                    );
                                                                }
                                                            )
                                                        )}
                                                    </List>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
                <Grid item md={9}>
                    <Stack
                        padding={'24px'}
                        spacing={1}
                        sx={{ background: common.white, borderRadius: '15px' }}
                    >
                        <Typography
                            variant="h5"
                            fontWeight={'bold'}
                            sx={{
                                display: 'flex',
                                gap: 1,
                                fontSize: '28px',
                                alignItems: 'center',
                                color: '#000000B2',
                            }}
                        >
                            {currentCampaign?.campaign}
                            {currentCampaign?.editable && (
                                <EditCampaign
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setOpenModalCampaign(true)}
                                />
                            )}
                        </Typography>
                        <Stack spacing={2}>
                            <Stack
                                spacing={2}
                                direction={'row'}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                    }}
                                >
                                    <CampaignStatus
                                        status={
                                            currentCampaign?.status_client as string
                                        }
                                    />
                                    {selectedPageId && (
                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                color: colors.main,
                                                backgroundColor:
                                                    'var(--color-primary-light-20)',
                                                padding: '6px 16px',
                                                borderRadius: '20px',
                                            }}
                                        >
                                            {
                                                questionPages.find(
                                                    (p) =>
                                                        p.id === selectedPageId
                                                )?.attributes.title
                                            }
                                        </Typography>
                                    )}
                                </Box>
                                <Stack
                                    gap={2}
                                    direction={'row'}
                                    display={
                                        isCampaignPublicWithCode
                                            ? 'none'
                                            : 'flex'
                                    }
                                >
                                    {/* <Button
                                        variant="contained"
                                        sx={styles.btnUploadFileExcel}
                                        startIcon={
                                            <Document
                                                style={{
                                                    color: 'var(--color-primary)',
                                                }}
                                            />
                                        }
                                        onClick={() =>
                                            setOpenUploadFileExcel(true)
                                        }
                                    >
                                        Tải bảng câu hỏi bằng Excel
                                    </Button> */}

                                    <IconButton
                                        size="small"
                                        aria-label="quotas"
                                        sx={styles.btnView}
                                        onClick={() => handleOpenSetupQuota()}
                                    >
                                        <NumbersIcon
                                            sx={{ color: colors.main }}
                                        />
                                    </IconButton>

                                    <IconButton
                                        size="small"
                                        aria-label="gift"
                                        sx={styles.btnView}
                                        onClick={() => handleOpenSetupReward()}
                                    >
                                        <CardGiftcardIcon
                                            sx={{ color: colors.main }}
                                        />
                                    </IconButton>

                                    <IconButton
                                        size="small"
                                        aria-label="bookmark"
                                        sx={styles.btnView}
                                        onClick={() =>
                                            navigate(
                                                `/survey/page-logic/${currentCampaign?.uuid}`
                                            )
                                        }
                                    >
                                        <RemoveRedEyeOutlinedIcon
                                            sx={{ color: colors.main }}
                                        />
                                    </IconButton>
                                </Stack>
                            </Stack>

                            {/* Questions Form */}
                            <Stack
                                sx={{
                                    maxHeight: 'calc(100vh - 400px)',
                                    overflowY: 'auto',
                                }}
                            >
                                {currentPageQuestions.length === 0 ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '200px',
                                            color: '#888',
                                        }}
                                    >
                                        <Typography>
                                            {selectedPageId
                                                ? 'Chưa có câu hỏi nào. Nhấn "Thêm câu hỏi" để bắt đầu.'
                                                : 'Vui lòng chọn một trang để xem câu hỏi.'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    currentPageQuestions.map((item, index) => (
                                        <QuestionNewEditForm
                                            methodsRef={methodsRef}
                                            handleItemSelect={handleItemSelect}
                                            selectedId={selectedIndex}
                                            index={index}
                                            key={`${selectedPageId}-${index}`}
                                            question={item}
                                            onDragStart={handleDragStart(item)}
                                            onDragOver={handleDragOver(item)}
                                            onDrop={handleDrop(item)}
                                            handleDeleteQuestion={
                                                handleDeleteQuestion
                                            }
                                            handleMarkQuestionAsRequired={
                                                handleMarkQuestionAsRequired
                                            }
                                            handleCopyQuestion={
                                                handleCopyQuestion
                                            }
                                            replaceQuestionByQuestionId={
                                                replaceQuestionByQuestionId
                                            }
                                            hasTwoOrMoreQuestions={
                                                currentPageQuestions.length > 1
                                            }
                                            questionPages={questionPages}
                                            currentPageId={selectedPageId}
                                        />
                                    ))
                                )}
                            </Stack>

                            {/* Footer Buttons */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'end',
                                    gap: 2,
                                }}
                            >
                                <Button
                                    type="button"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleSaveSelectedQuestion()}
                                    startIcon={<SaveQuestionIcon />}
                                    sx={{
                                        ...styles.btnFooter,
                                        marginRight: 0,
                                    }}
                                    disabled={!selectedPageId}
                                >
                                    Lưu
                                </Button>
                                {currentPageQuestions.length < 80 && (
                                    <Button
                                        type="button"
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddQuestionIcon />}
                                        onClick={() => handleAddQuestion()}
                                        sx={styles.btnFooter}
                                        disabled={!selectedPageId}
                                    >
                                        Thêm câu hỏi
                                    </Button>
                                )}
                            </Box>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>

            {/* Modals */}
            {openUploadFileExcel && (
                <ModalUplooadFileQuestion
                    open={openUploadFileExcel}
                    onClose={() => setOpenUploadFileExcel(false)}
                    getListQuestion={fetchQuestionPages}
                />
            )}
            {isModalOpenDrag && (
                <ModalConfirmSwapQuestion
                    open={isModalOpenDrag}
                    onClose={closeModal}
                    onOk={confirmSwap}
                    dragSource={dragSource}
                />
            )}
            {openModalCampaign && (
                <CampaignForm
                    actionType={'edit'}
                    open={openModalCampaign}
                    onClose={() => setOpenModalCampaign(false)}
                />
            )}
            <ModalConfirm
                open={openModalConfirmDeletePage}
                onClose={() => setOpenModalConfirmDeletePage(false)}
                onOk={() => handleDeletePage(pageToDelete.id)}
                title={`Bạn có chắc chắn muốn xóa trang "${pageToDelete?.attributes?.title}" này?`}
                content={'Nếu xóa trang, tất cả câu hỏi trong trang sẽ bị xóa.'}
            />
            {isOpenSetupQuota && (
                <QuotaForm
                    open={isOpenSetupQuota}
                    questionPages={questionPages}
                    onClose={() => setIsOpenSetupQuota(false)}
                />
            )}
            {isOpenSetupReward && (
                <RewardForm
                    open={isOpenSetupReward}
                    onClose={() => setIsOpenSetupReward(false)}
                />
            )}
        </CampaignContainer>
    );
};

const styles = createStyleSheet({
    btnUploadFileExcel: {
        borderRadius: '10px',
        minWidth: '328px',
        textTransform: 'none',
        height: 53,
        fontSize: '18px',
        ':hover': {
            background: colors.main,
        },
    },
    btnView: {
        borderRadius: '10px',
        width: '55px',
        border: `2px solid ${colors.main}`,
    },
    btnFooter: {
        marginRight: '70px',
        borderRadius: '40px',
        minWidth: '230px',
        textTransform: 'none',
        height: 60,
        fontSize: '20px',
    },
});

export default CampaignDesignPageLogic;
